// The job's service location on customer documents (quote PDF, invoice PDF, invoice email, pay
// page) — the ONE owner (operator ruling 2026-10-24 #4, boards #6180/#6204/#6215; PRODUCT_DECISIONS
// "Customer documents and links" #1: print a "Service location" block whenever it differs from
// billing).
//
// Source: work_orders.service_address_line_1/_2, service_city, service_state, service_zip_code
// (an invoice has no address of its own; it uses its work order's). Billing: the customer's
// billing_* columns, else the legacy street_address/city/state/zip_code family (a DB trigger keeps
// them equal; either can be the populated one on an older row).
//
// Returns null — the document keeps its existing look — when the job has no service address or
// it is the billing address. "Same" ignores case, punctuation and spacing, accepts the unit either
// in line 2 or packed into line 1 ("1835 SE Hawthorne Blvd, Apt 3"), compares states as 2-letter
// codes and ZIPs on their first 5 digits, and skips state/ZIP when either side lacks them.

import { normalizeStateToCode } from './usStates';

const clean = (v) => String(v ?? '').trim();
const words = (v) => clean(v).toLowerCase().replace(/\./g, '').replace(/[,#]/g, ' ').replace(/\s+/g, ' ').trim();
const zip5 = (v) => clean(v).replace(/[^0-9]/g, '').slice(0, 5);
const stateKey = (v) => normalizeStateToCode(clean(v)).toLowerCase();

/** The work order's service address as lines, or null when it has no street line. */
export function serviceAddressLines(workOrder) {
  if (!workOrder) return null;
  const line1 = clean(workOrder.service_address_line_1);
  if (!line1) return null;
  const line2 = clean(workOrder.service_address_line_2);
  const city = clean(workOrder.service_city);
  const state = clean(workOrder.service_state);
  const zip = clean(workOrder.service_zip_code);
  const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [line1, line2, cityStateZip].filter(Boolean);
}

const billingParts = (customer) => {
  if (!customer) return null;
  const line1 = clean(customer.billing_address_line_1 || customer.street_address);
  if (!line1) return null;
  return {
    street: words(`${line1} ${clean(customer.billing_address_line_2)}`),
    city: words(customer.billing_city || customer.city),
    state: stateKey(customer.billing_state || customer.state),
    zip: zip5(customer.billing_zip_code || customer.zip_code),
  };
};

/**
 * @param {object} workOrder  carries service_address_line_1/_2, service_city, service_state, service_zip_code
 * @param {object} customer   carries billing_* (or legacy street_address/city/state/zip_code)
 * @returns {string[] | null} the lines to print under "Service location", or null for none
 */
export function documentServiceLocation(workOrder, customer) {
  const lines = serviceAddressLines(workOrder);
  if (!lines) return null;
  const billing = billingParts(customer);
  if (!billing) return lines;
  const street = words(`${workOrder.service_address_line_1} ${clean(workOrder.service_address_line_2)}`);
  const city = words(workOrder.service_city);
  const state = stateKey(workOrder.service_state);
  const zip = zip5(workOrder.service_zip_code);
  const same = street === billing.street
    && city === billing.city
    && (!state || !billing.state || state === billing.state)
    && (!zip || !billing.zip || zip === billing.zip);
  return same ? null : lines;
}
